/**
 * Gets the number of decimal places in a number
 *
 * @param value The number to check
 *
 * @returns How many decimal places the number has. For example, 1.23 returns 2.
 */
export function getDecimalPlaces(value: number): number {
  const match = `${value}`.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);

  if (!match) {
    return 0;
  }

  return Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0) -
      // Adjust for scientific notation.
      (match[2] ? +match[2] : 0)
  );
}

/**
 * Rounds a number to the nearest value mulptiple of step.
 *
 * @param number The number to round.
 * @param step The multiple to round to.
 *
 * @returns A rounded number. For example, round(1.23, 0.1) returns 1.2.
 */
export function round(number: number, step: number): number {
  if (step < 1) {
    const places = getDecimalPlaces(step);
    return parseFloat(number.toFixed(places));
  }
  return Math.round(number / step) * step;
}

/**
 * Floors a number to the nearest value multiple of step.
 *
 * @param number The number to floor.
 * @param step The multiple to floor to.
 *
 * @returns A floored number. For example, floor(1.27, 0.1) returns 1.2.
 */
export function floor(number: number, step: number): number {
  const flooredNumber = Math.floor(number / step) * step;

  if (step < 1) {
    const places = getDecimalPlaces(step);
    return parseFloat(flooredNumber.toFixed(places));
  }
  return flooredNumber;
}
